/**
 * Convert persisted tableView content into a display row array.
 *
 * UUID-keyed collections (merge-patch safe) are sorted newest-first by
 * `_created` when present, then returned without that meta key. Legacy row
 * arrays pass through unchanged. `{ rows }` provider shapes return `value.rows`.
 */
export const collectionToTableRows = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return [];
  }
  // { rows, rowCount } shape used by some computed value providers
  if (Array.isArray(value.rows)) {
    return value.rows;
  }
  // Read via a key variable so we avoid both no-underscore-dangle and
  // the autofix that wants a._created / a["_created"].
  const createdKey = '_created';
  const createdSortKey = createdValue => {
    if (createdValue == null) {
      return null;
    }
    if (typeof createdValue === 'number') {
      return createdValue;
    }
    if (typeof createdValue === 'string') {
      const parsed = Date.parse(createdValue);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };
  return Object.entries(value).filter(([, item]) => item && typeof item === 'object' && !Array.isArray(item)).sort(([, a], [, b]) => {
    const aKey = createdSortKey(a[createdKey]);
    const bKey = createdSortKey(b[createdKey]);
    if (aKey == null && bKey == null) {
      return 0;
    }
    if (aKey == null) {
      return 1;
    }
    if (bKey == null) {
      return -1;
    }
    return bKey - aKey;
  }).map(([, item]) => Object.fromEntries(Object.entries(item).filter(([key]) => key !== '_created')));
};

// Helper to get all unique headers from rows, preserving order per row.
// If an early row omits a key (e.g. null omitted) but a later row includes it between other
// columns, insert that key before the leftmost existing header that appears after it in that row
// — instead of appending at the end.
export const getAllHeaders = rows => {
  if (!rows || rows.length === 0) return [];
  const headersSet = new Set();
  const headers = [];
  rows.forEach(row => {
    const rowKeys = Object.keys(row);
    rowKeys.forEach((key, i) => {
      if (headersSet.has(key)) return;
      let insertAt = headers.length;
      const keysAfter = rowKeys.slice(i + 1);
      let minKnownAfterIndex = Infinity;
      keysAfter.forEach(k => {
        if (headersSet.has(k)) {
          const idx = headers.indexOf(k);
          if (idx < minKnownAfterIndex) minKnownAfterIndex = idx;
        }
      });
      if (minKnownAfterIndex !== Infinity) {
        insertAt = minKnownAfterIndex;
      }
      headers.splice(insertAt, 0, key);
      headersSet.add(key);
    });
  });
  return headers;
};
const formatNumberForDisplay = v => {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v.toLocaleString('en-US');
  }
  return null;
};

// Helper to normalize cell values - converts single-element JSON arrays to plain strings
export const normalizeValue = val => {
  if (val == null) return '';
  const numStr = formatNumberForDisplay(val);
  if (numStr != null) return numStr;
  const str = String(val);
  // Try to parse as JSON if it looks like an array
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        // Single element array - return just the value
        if (parsed.length === 1) {
          const one = parsed[0];
          const formatted = formatNumberForDisplay(one);
          return formatted != null ? formatted : String(one !== null && one !== void 0 ? one : '');
        }
        // Multi-element array - join with comma
        if (parsed.length > 1) {
          return parsed.map(v => {
            var _formatNumberForDispl;
            return (_formatNumberForDispl = formatNumberForDisplay(v)) !== null && _formatNumberForDispl !== void 0 ? _formatNumberForDispl : String(v !== null && v !== void 0 ? v : '');
          }).join(', ');
        }
        // Empty array
        return '';
      }
    } catch (_unused) {
      // Not valid JSON, return original
    }
  }
  return str;
};

// Helper to convert rows to CSV and trigger download
export const downloadCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  if (typeof window === 'undefined' || !window.URL || !window.URL.createObjectURL) {
    return;
  }
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  const headers = getAllHeaders(rows);
  const csvContent = [headers.join(','), ...rows.map(row => headers.map(h => {
    const val = normalizeValue(row[h]);
    // Escape quotes and wrap in quotes if contains comma, quote, or newline
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  }).join(','))].join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.setAttribute('download', `${filename}.csv`);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(blobUrl);
};